import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import SelectableCard from 'core/components/SelectableCard'
import Info from 'core/components/validatedForm/Info'
import ListTableField from 'core/components/validatedForm/ListTableField'
import Radio from 'core/elements/input/Radio'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip/Tooltip'
import useListAction from 'core/hooks/useListAction'
import { listNetworks } from '../../networks/actions'
import {
  MANAGED_NETWORK_INFO,
  NetworkMode,
  SIMPLE_NETWORK_TOOLTIP_INFO,
} from '../../networks/constants'
import { networksSelector } from '../../networks/selectors'
import {
  filterCreateVmNetworks,
  filterNetworksByClusterLabels,
  getCreateVmNetworkCounts,
  syncNetworkAttachments,
} from '../CreateVirtualMachineModal.helpers'
import useCreateVirtualMachineModalStyles from '../CreateVirtualMachineModal.styles'
import {
  getManagedNetworkColumns,
  getManagedNetworkRowExtension,
  getSimpleNetworkColumns,
} from '../columns/networkColumns'

interface NetworkingControllerInput {
  clusterId: string | null
  applicableNetworkLabels: string[]
  currentTenant: string
  isSelfServiceUser: boolean
}

interface Props {
  getParamsUpdater: any
  loadingNetworks: boolean
  networksList: any[]
  params: any
  setMinAvailableIpsNetwork: (value: any) => void
  setVmNetworkMode: (value: NetworkMode | null) => void
  updateParams: any
  vmNetworkMode: NetworkMode | null
}

export function useCreateVirtualMachineNetworkingStepController({
  clusterId,
  applicableNetworkLabels,
  currentTenant,
  isSelfServiceUser,
}: NetworkingControllerInput) {
  const [vmNetworkMode, setVmNetworkMode] = useState<NetworkMode | null>(NetworkMode.MANAGED)

  const { loading: loadingNetworks, reload: reloadNetworks } = useListAction(listNetworks, {
    initialLoadingState: true,
  })
  const networks = useSelector(networksSelector)

  // FR-014, FR-016: Build the list of networks the user is allowed to pick.
  // First keep only networks for this tenant (project), then keep only the ones
  // whose labels match what the chosen cluster's physical network can host.
  const networksList = useMemo(() => {
    const tenantFiltered = filterCreateVmNetworks({
      networks,
      currentTenant,
      isSelfServiceUser,
    })
    return filterNetworksByClusterLabels(tenantFiltered, clusterId, applicableNetworkLabels)
  }, [clusterId, applicableNetworkLabels, currentTenant, isSelfServiceUser, networks])

  const resetNetworkingStepState = () => {
    setVmNetworkMode(NetworkMode.MANAGED)
  }

  return {
    loadingNetworks,
    networksList,
    reloadNetworks,
    resetNetworkingStepState,
    setVmNetworkMode,
    vmNetworkMode,
  }
}

export default function CreateVirtualMachineNetworkingStep({
  getParamsUpdater,
  loadingNetworks,
  networksList,
  params,
  setMinAvailableIpsNetwork,
  setVmNetworkMode,
  updateParams,
  vmNetworkMode,
}: Props) {
  const classes = useCreateVirtualMachineModalStyles()
  const { reload: reloadNetworks } = useListAction(listNetworks, {
    initialLoadingState: true,
  })
  const { managedNetworksCount, simpleNetworksCount } = useMemo(
    () => getCreateVmNetworkCounts(networksList),
    [networksList],
  )

  const simpleNetworkColumns = useMemo(
    () => getSimpleNetworkColumns({ params, getParamsUpdater }),
    [getParamsUpdater, params],
  )

  const managedNetworkColumns = useMemo(
    () => getManagedNetworkColumns({ params, getParamsUpdater }),
    [getParamsUpdater, params],
  )

  const networkColumns = useMemo(() => {
    return vmNetworkMode === NetworkMode.SIMPLE ? simpleNetworkColumns : managedNetworkColumns
  }, [managedNetworkColumns, simpleNetworkColumns, vmNetworkMode])

  // FR-014: Extra UI shown under each selected network row: the additional NIC rows
  // (rows 2 and onward) plus the "+ Add subnet for {name}" button. Each row is one
  // network interface (NIC). This only appears in Managed mode (FR-020); Layer 2
  // networks don't have subnets to add.
  const managedRowExtension = useMemo(
    () =>
      vmNetworkMode === NetworkMode.MANAGED
        ? getManagedNetworkRowExtension({ params, getParamsUpdater })
        : undefined,
    [getParamsUpdater, params, vmNetworkMode],
  )

  const filteredNetworksList = useMemo(() => {
    if (vmNetworkMode === NetworkMode.SIMPLE) {
      return networksList.filter((network) => network.isSimpleNetwork)
    }

    if (vmNetworkMode === NetworkMode.MANAGED) {
      return networksList.filter(
        (network) => !network.isSimpleNetwork && network.subnets?.length > 0,
      )
    }

    return networksList
  }, [networksList, vmNetworkMode])

  return (
    <div className={classes.fields}>
      <Info className={classes.info}>
        <div className={classes.infoContent}>
          <Text variant="caption1">Networking</Text>
          <Text variant="body2">
            Assign a network to the VM by selecting from available network pools.
          </Text>
        </div>
      </Info>

      <Text variant="body2" style={{ marginBottom: '16px', display: 'block', fontWeight: '600' }}>
        Select Network Type
      </Text>
      <div className={classes.networkModeCards}>
        <div className={classes.networkCardWrapper}>
          <SelectableCard
            id="managed"
            onClick={() => {
              setVmNetworkMode(NetworkMode.MANAGED)
              if (params.networks.length > 0) {
                getParamsUpdater('networks')([])
              }
              getParamsUpdater('selectedSubnets')({})
              // FR-017: switching network type wipes any NIC rows the user set up,
              // so we don't carry stale picks across modes.
              getParamsUpdater('networkAttachments')({})
            }}
            active={vmNetworkMode === NetworkMode.MANAGED}
            className={classes.networkCard}
            withCustomBody
          >
            <div className={classes.networkCardBody}>
              <Radio checked={vmNetworkMode === NetworkMode.MANAGED} />
              <Text variant="caption1" className={classes.networkCardContent}>
                Managed Networks ({managedNetworksCount})
              </Text>
              <Tooltip customClassName={classes.networkTooltipBody} message={MANAGED_NETWORK_INFO}>
                <HelpOutlineIcon className={classes.networkInfoIcon} />
              </Tooltip>
            </div>
          </SelectableCard>
        </div>
        <div className={classes.networkCardWrapper}>
          <SelectableCard
            id="simple"
            onClick={() => {
              setVmNetworkMode(NetworkMode.SIMPLE)
              if (params.networks.length > 0) {
                getParamsUpdater('networks')([])
              }

              if (params.useSecurityGroups) {
                updateParams({ useSecurityGroups: false, securityGroups: [] })
              }

              getParamsUpdater('selectedSubnets')({})
              // FR-017: switching network type wipes any NIC rows the user set up,
              // so we don't carry stale picks across modes.
              getParamsUpdater('networkAttachments')({})
            }}
            active={vmNetworkMode === NetworkMode.SIMPLE}
            className={classes.networkCard}
            withCustomBody
          >
            <div className={classes.networkCardBody}>
              <Radio checked={vmNetworkMode === NetworkMode.SIMPLE} />
              <Text variant="caption1" className={classes.networkCardContent}>
                Layer 2 Networks ({simpleNetworksCount})
              </Text>
              <Tooltip
                customClassName={classes.networkTooltipBody}
                message={SIMPLE_NETWORK_TOOLTIP_INFO}
              >
                <HelpOutlineIcon className={classes.networkInfoIcon} />
              </Tooltip>
            </div>
          </SelectableCard>
        </div>
      </div>

      <ListTableField
        id="networks"
        data={filteredNetworksList}
        loading={loadingNetworks}
        columns={networkColumns}
        renderRowExtension={managedRowExtension}
        onReload={reloadNetworks}
        onChange={(selectedNetworks) => {
          setMinAvailableIpsNetwork(null)
          getParamsUpdater('networks')(selectedNetworks)

          // Rebuild the subnet picks so they match the networks that are still selected:
          // drop entries for unchecked networks, and default any newly checked network
          // to '' which means "Automatic" (let the system pick the subnet).
          const updatedSubnets = Object.fromEntries(
            selectedNetworks.map((n) => [n.id, params.selectedSubnets?.[n.id] ?? '']),
          )
          getParamsUpdater('selectedSubnets')(updatedSubnets)

          // FR-017: keep the NIC rows in step with the checked networks — add one
          // default row (one NIC) for each newly checked network, and drop the rows
          // belonging to networks the user just unchecked.
          getParamsUpdater('networkAttachments')(
            syncNetworkAttachments(selectedNetworks, params.networkAttachments ?? {}),
          )
        }}
        value={params.networks}
        uniqueIdentifier="id"
        searchTargets={['name']}
        multiSelection
        noScroll
        required
        checkboxCond={(network) => {
          if (vmNetworkMode === null) {
            return true
          }

          if (vmNetworkMode === NetworkMode.SIMPLE) {
            return network.isSimpleNetwork
          }

          if (vmNetworkMode === NetworkMode.MANAGED) {
            if (network?.availablePortAndIPs <= 0) {
              return false
            }

            return !network.isSimpleNetwork && network.subnets?.length > 0
          }

          return true
        }}
        checkboxCondMessage={
          vmNetworkMode === NetworkMode.SIMPLE
            ? 'Only Simple Networks can be selected in Simple Network mode'
            : vmNetworkMode === NetworkMode.MANAGED
              ? 'Managed Network has no available IP addresses or ports'
              : 'Select a network mode first'
        }
      />
    </div>
  )
}
